import React, { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Calendar, MapPin, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAdminStore } from '@/lib/admin-store';
import { useTranslation } from '@/hooks/useTranslation';
import { formatDate as formatLocalizedDate } from '@/lib/format-date';

const ExhibitionDetailPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { t, language, localize } = useTranslation();
  const exhibitions = useAdminStore((s) => s.exhibitions);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);

  const exhibition = useMemo(
    () => exhibitions.find((e) => e.slug === slug),
    [exhibitions, slug],
  );

  if (!exhibition) {
    return (
      <div className="min-h-screen pt-32 pb-20 px-4 sm:px-6 md:px-24 text-center">
        <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-black/20 dark:text-white/20">
          Exhibition not found
        </p>
        <Link
          to="/exhibitions"
          className="mt-4 inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black/40 dark:text-white/40 hover:text-black dark:hover:text-white transition-colors"
        >
          <ArrowLeft size={14} /> Back to Exhibitions
        </Link>
      </div>
    );
  }

  const now = new Date();
  const start = new Date(exhibition.startDate);
  const end = new Date(exhibition.endDate);
  const status = now >= start && now <= end ? 'current' : start > now ? 'upcoming' : 'archive';

  const statusLabel = status === 'current' ? t.exhibitions.current : status === 'upcoming' ? t.exhibitions.upcoming : t.exhibitions.archive;
  const statusColors = status === 'current'
    ? 'bg-green-500/20 text-green-400'
    : status === 'upcoming'
      ? 'bg-blue-500/20 text-blue-300'
      : 'bg-white/10 text-white/50';

  const formatDate = (date: string): string => formatLocalizedDate(date, language);

  const galleryImages = exhibition.images.length > 0 ? exhibition.images : (exhibition.poster ? [exhibition.poster] : []);

  const openLightbox = (index: number): void => setLightboxIndex(index);
  const closeLightbox = (): void => setLightboxIndex(null);
  const prevImage = (): void => {
    if (lightboxIndex === null) return;
    setLightboxIndex(lightboxIndex === 0 ? galleryImages.length - 1 : lightboxIndex - 1);
  };
  const nextImage = (): void => {
    if (lightboxIndex === null) return;
    setLightboxIndex(lightboxIndex === galleryImages.length - 1 ? 0 : lightboxIndex + 1);
  };

  return (
    <div className="min-h-screen bg-white dark:bg-black">
      {/* Hero */}
      <div className="relative h-[50vh] sm:h-[60vh] md:h-[70vh] overflow-hidden">
        <img
          src={exhibition.poster}
          alt={localize(exhibition.title)}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10" />
        <div className="absolute bottom-0 left-0 right-0 p-6 sm:p-10 md:p-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[9px] font-bold uppercase tracking-[0.15em] ${statusColors} mb-4`}>
              {status === 'current' && <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />}
              {statusLabel}
            </span>
            <h1 className="text-3xl sm:text-5xl md:text-7xl font-syncopate font-bold text-white uppercase tracking-tighter leading-none mb-4">
              {localize(exhibition.title)}
            </h1>
            <div className="flex flex-col sm:flex-row gap-3 sm:gap-6 text-[10px] font-bold uppercase tracking-widest text-white/50">
              <span className="flex items-center gap-2">
                <Calendar size={14} />
                {formatDate(exhibition.startDate)} &mdash; {formatDate(exhibition.endDate)}
              </span>
              <span className="flex items-center gap-2">
                <MapPin size={14} />
                {localize(exhibition.location)}
              </span>
            </div>
          </motion.div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-24 py-12 sm:py-20">
        {/* Back link */}
        <motion.div
          initial={{ opacity: 0, x: -10 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4 }}
          className="mb-10"
        >
          <Link
            to="/exhibitions"
            className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black/30 dark:text-white/30 hover:text-black dark:hover:text-white transition-colors"
          >
            <ArrowLeft size={14} />
            {t.exhibitions.title}
          </Link>
        </motion.div>

        {/* Description */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="mb-16 sm:mb-20"
        >
          <h2 className="text-[10px] font-bold uppercase tracking-[0.4em] text-black/25 dark:text-white/25 mb-4">
            {t.exhibitions.aboutExhibition}
          </h2>
          <p className="text-base sm:text-lg md:text-xl text-black/60 dark:text-white/60 font-light leading-relaxed max-w-4xl whitespace-pre-line text-justify">
            {localize(exhibition.description)}
          </p>
        </motion.div>

        {/* About the Artist */}
        {(exhibition.aboutArtist?.en || exhibition.aboutArtist?.ka) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25, duration: 0.6 }}
            className="mb-16 sm:mb-20"
          >
            <h2 className="text-[10px] font-bold uppercase tracking-[0.4em] text-black/25 dark:text-white/25 mb-4">
              {t.exhibitions.aboutArtist}
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-black/60 dark:text-white/60 font-light leading-relaxed max-w-4xl whitespace-pre-line text-justify">
              {localize(exhibition.aboutArtist)}
            </p>
          </motion.div>
        )}

        {/* Gallery */}
        {galleryImages.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.6 }}
            className="mb-16 sm:mb-20"
          >
            <h2 className="text-[10px] font-bold uppercase tracking-[0.4em] text-black/25 dark:text-white/25 mb-6">
              {t.exhibitions.gallery}
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 sm:gap-4">
              {galleryImages.map((img, i) => (
                <motion.button
                  key={i}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 + i * 0.08, duration: 0.5 }}
                  onClick={() => openLightbox(i)}
                  className="group aspect-[4/3] rounded-2xl overflow-hidden bg-[#f0f0f0] dark:bg-[#0a0a0a] border border-black/5 dark:border-white/5"
                >
                  <img
                    src={img}
                    alt={`${localize(exhibition.title)} — ${i + 1}`}
                    className="w-full h-full object-cover grayscale-[0.2] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700"
                  />
                </motion.button>
              ))}
            </div>
          </motion.div>
        )}

        {/* Custom Section */}
        {exhibition.customSection?.enabled && (localize(exhibition.customSection.title) || localize(exhibition.customSection.text)) && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35, duration: 0.6 }}
            className="mb-16 sm:mb-20"
          >
            {localize(exhibition.customSection.title) && (
              <h2 className="text-[10px] font-bold uppercase tracking-[0.4em] text-black/25 dark:text-white/25 mb-4">
                {localize(exhibition.customSection.title)}
              </h2>
            )}
            <p className="text-base sm:text-lg md:text-xl text-black/60 dark:text-white/60 font-light leading-relaxed max-w-4xl whitespace-pre-line text-justify">
              {localize(exhibition.customSection.text)}
            </p>
          </motion.div>
        )}

        {/* Participating Artists */}
        {exhibition.artists.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.6 }}
          >
            <h2 className="text-[10px] font-bold uppercase tracking-[0.4em] text-black/25 dark:text-white/25 mb-6">
              {t.exhibitions.participatingArtists}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
              {exhibition.artists.map((artist) => (
                <Link
                  key={artist._id}
                  to={`/artists/${artist.slug}`}
                  className="group flex items-center gap-4 p-4 rounded-2xl border border-black/[0.04] dark:border-white/[0.04] hover:border-black/10 dark:hover:border-white/10 transition-all"
                >
                  <img
                    src={artist.portrait}
                    alt={localize(artist.name)}
                    className="w-16 h-16 rounded-full object-cover grayscale-[0.3] group-hover:grayscale-0 transition-all duration-500"
                  />
                  <div>
                    <h3 className="text-sm font-bold text-black dark:text-white group-hover:opacity-70 transition-opacity">
                      {localize(artist.name)}
                    </h3>
                    <p className="text-[10px] text-black/40 dark:text-white/40 line-clamp-2 mt-1">
                      {localize(artist.bio)}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightboxIndex !== null && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[900] bg-black/95 flex items-center justify-center"
            onClick={closeLightbox}
          >
            <button
              onClick={closeLightbox}
              className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors z-10"
            >
              <X size={20} />
            </button>
            {galleryImages.length > 1 && (
              <>
                <button
                  onClick={(e) => { e.stopPropagation(); prevImage(); }}
                  className="absolute left-4 sm:left-8 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors z-10"
                >
                  <ChevronLeft size={20} />
                </button>
                <button
                  onClick={(e) => { e.stopPropagation(); nextImage(); }}
                  className="absolute right-4 sm:right-8 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-white/10 text-white flex items-center justify-center hover:bg-white/20 transition-colors z-10"
                >
                  <ChevronRight size={20} />
                </button>
              </>
            )}
            <motion.img
              key={lightboxIndex}
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.3 }}
              src={galleryImages[lightboxIndex]}
              alt=""
              className="max-w-[90vw] max-h-[85vh] object-contain rounded-lg"
              onClick={(e) => e.stopPropagation()}
            />
            <span className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] font-bold uppercase tracking-widest text-white/30">
              {lightboxIndex + 1} / {galleryImages.length}
            </span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExhibitionDetailPage;
